import {
  IconArrowLeft,
  IconCirclePlus,
  IconFilter,
  IconTrash,
} from '@tabler/icons-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BasicTable,
  Loading,
  ScalrEmptyBox,
  SingleBranchSelect,
  TreeSelector,
  TreeSelectorChild,
  TreeSelectorParent,
} from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import type { ScalrTableColumn } from '@/components/customComponents'
import { useKpiUserGoalAssignments } from '@/hooks'
import { TranslationFunctionType } from '@/i18n'
import { pushNotification, useAppDispatch } from '@/store'
import { BranchKpisModel, KpiUserAssignmentCreationModel } from '@/types'
import { formatValueForKpi } from '@/utils'

interface Selectable {
  id: string
  name: string
}

type AdditionSelections = {
  branch?: string // Branch name from SingleBranchSelect
  kpi?: Selectable
}

interface CreateGoalPlanProps {
  onCancel: () => void
  onSave: () => void
}

interface KpiUserAssignmentData {
  id: number
  branchKpiAssignmentId: string // This is the branchKpi.id
  userId: number
  // Display fields
  userName: string
  kpiLabel: string // Full display label from tree
  branchName: string
  targetValue: number
  bonusAmount: number
  kpiUnit: string
}

const getKpiAddFormColumns = (
  t: TranslationFunctionType
): ScalrTableColumn[] => [
  {
    accessorKey: 'branch',
    header: t('targets.table.branch'),
    type: 'text',
  },
  {
    accessorKey: 'kpiLabel',
    header: t('targets.table.kpiName'),
    type: 'text',
  },
  {
    accessorKey: 'user',
    header: t('targets.table.user'),
    type: 'text',
  },
  {
    accessorKey: 'target',
    header: t('targets.table.target'),
    type: 'text',
  },
  {
    accessorKey: 'bonus',
    header: t('targets.table.bonus'),
    type: 'percentage',
  },
]

export const CreateUserGoalPlan = ({
  onCancel,
  onSave,
}: CreateGoalPlanProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [additionSelections, setAdditionSelections] =
    useState<AdditionSelections>({
      branch: undefined,
      kpi: undefined,
    })
  const [kpiUserAssignments, setKpiUserAssignments] = useState<
    KpiUserAssignmentData[]
  >([])
  const [selectedKpiId, setSelectedKpiId] = useState<string>()
  const [selectedUserId, setSelectedUserId] = useState<string>()
  const [bonusAmount, setBonusAmount] = useState(0)
  const [isSaving, setIsSaving] = useState(false)

  const { fetchMetadata, createUserAssignments, metadata, loading } =
    useKpiUserGoalAssignments()

  // Transform metadata.branchKpis to TreeSelector format grouped by kpiGroup
  const kpiTreeData: TreeSelectorParent[] = useMemo(() => {
    if (!metadata?.branchKpis) return []

    // Group branchKpis by kpiGroup
    const grouped = metadata.branchKpis.reduce(
      (acc, kpi) => {
        const group = kpi.kpiGroup
        if (!acc[group]) acc[group] = []
        acc[group].push(kpi)
        return acc
      },
      {} as Record<string, BranchKpisModel[]>
    )

    return Object.entries(grouped).map(([group, kpis]) => ({
      id: group,
      label: group,
      children: kpis.map((kpi) => ({
        id: kpi.id, // Use branchKpi id for selection
        label: `${kpi.kpiMetric} (${kpi.kpiUnit}) - ${kpi.year} Q${kpi.quarter}`,
        disabled: false,
      })),
    }))
  }, [metadata])

  // Transform metadata.eligibleUsers to flat list format
  const userFlatData: TreeSelectorChild[] = useMemo(() => {
    if (!metadata?.eligibleUsers) return []

    return metadata.eligibleUsers.map((user) => ({
      id: user.id.toString(),
      label: user.fullName || user.username,
      disabled: false,
    }))
  }, [metadata])

  // Validate if all required fields are filled
  const isFormValid = useMemo(() => {
    return (
      !!additionSelections.branch &&
      !!selectedKpiId &&
      !!selectedUserId &&
      bonusAmount > 0
    )
  }, [additionSelections.branch, selectedKpiId, selectedUserId, bonusAmount])

  const handleAddKPI = useCallback(() => {
    const selectedBranchName = additionSelections.branch

    if (
      !selectedBranchName ||
      !selectedKpiId ||
      !selectedUserId ||
      !metadata ||
      bonusAmount <= 0
    ) {
      return
    }

    // Find user from metadata
    const selectedUser = metadata?.eligibleUsers?.find(
      (user) => user.id.toString() === selectedUserId
    )
    if (!selectedUser) {
      return
    }

    // Find branchKpi by selectedKpiId (which is now branchKpi.id)
    const selectedBranchKpi = metadata?.branchKpis?.find(
      (kpi) => kpi.id === selectedKpiId
    )
    if (!selectedBranchKpi) {
      return
    }

    const newAssignment: KpiUserAssignmentData = {
      id: kpiUserAssignments.length + 1,
      branchKpiAssignmentId: selectedBranchKpi.id,
      userId: selectedUser.id,
      userName: selectedUser.fullName || selectedUser.username,
      kpiLabel: `${selectedBranchKpi.kpiMetric} (${selectedBranchKpi.kpiUnit}) - ${selectedBranchKpi.year} Q${selectedBranchKpi.quarter}`,
      branchName: selectedBranchName,
      targetValue: selectedBranchKpi.targetValue,
      bonusAmount: bonusAmount,
      kpiUnit: selectedBranchKpi.kpiUnit,
    }
    setKpiUserAssignments((prev) => [...prev, newAssignment])

    // Reset form fields after adding
    setSelectedKpiId(undefined)
    setSelectedUserId(undefined)
    setBonusAmount(0)
  }, [
    kpiUserAssignments,
    additionSelections.branch,
    selectedKpiId,
    selectedUserId,
    bonusAmount,
    metadata,
  ])

  const handleRemoveKPI = useCallback((id: number | string) => {
    setKpiUserAssignments((prev) =>
      prev.filter((assignment) => assignment.id !== id)
    )
  }, [])

  const resetAdditionForm = useCallback(() => {
    setKpiUserAssignments([])
    setAdditionSelections({
      branch: undefined,
      kpi: undefined,
    })
    setSelectedKpiId(undefined)
    setSelectedUserId(undefined)
    setBonusAmount(0)
  }, [])

  const handleCancel = useCallback(() => {
    const hasChanges =
      additionSelections.branch ||
      selectedKpiId ||
      selectedUserId ||
      kpiUserAssignments.length > 0 ||
      bonusAmount > 0

    if (hasChanges) {
      const confirmClose = window.confirm(t('targets.form.closeWithoutSaving'))
      if (confirmClose) {
        resetAdditionForm()
        onCancel()
      }
    } else {
      resetAdditionForm()
      onCancel()
    }
  }, [
    additionSelections.branch,
    selectedKpiId,
    selectedUserId,
    kpiUserAssignments,
    bonusAmount,
    resetAdditionForm,
    onCancel,
    t,
  ])

  const handleSave = useCallback(async () => {
    const assignmentsToSave: KpiUserAssignmentCreationModel[] =
      kpiUserAssignments.map((assignment) => ({
        branchKpiAssignmentId: assignment.branchKpiAssignmentId,
        user: assignment.userId,
        bonusAmount: assignment.bonusAmount,
      }))

    setIsSaving(true)
    try {
      await createUserAssignments(assignmentsToSave)
      dispatch(
        pushNotification({
          type: 'success',
          title: t('targets.success.userAssignmentsSaved'),
          duration: 3000,
        })
      )
      resetAdditionForm()
      onSave()
    } catch (error) {
      dispatch(
        pushNotification({
          type: 'error',
          title: t('targets.errors.errorCreatingUserAssignment'),
          description:
            error instanceof Error
              ? error.message
              : t('common.errors.serverError'),
          duration: 5000,
        })
      )
    } finally {
      setIsSaving(false)
    }
  }, [kpiUserAssignments, createUserAssignments, resetAdditionForm, onSave])

  const handleBranchSelect = useCallback(
    (branchName: string) => {
      setAdditionSelections((prev) => ({ ...prev, branch: branchName }))
      // Fetch metadata for the selected branch
      fetchMetadata(branchName)
    },
    [fetchMetadata]
  )

  const handleKpiTreeSelection = useCallback(
    (kpiId: string, categoryId: string) => {
      setSelectedKpiId(kpiId)
    },
    []
  )

  const handleUserSelection = useCallback((userId: string) => {
    setSelectedUserId(userId)
  }, [])

  return (
    <div className="my-6 relative">
      {isSaving && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-sm rounded-lg min-h-[200px]">
          <Loading />
        </div>
      )}
      <div className="flex items-center gap-4 mb-4">
        <Button onClick={handleCancel} disabled={isSaving}>
          <IconArrowLeft className="mr-2" />
          {t('common.buttons.back')}
        </Button>
        <h3 className="leading-none tracking-tight text-black-500 text-display-s">
          {t('targets.form.createUserGoalPlan')}
        </h3>
      </div>

      <div>
        {/* Form Section */}
        <Card>
          <CardContent className="p-4">
            {!metadata && loading ? (
              <Loading />
            ) : (
              <div className="flex flex-col md:flex-row gap-6">
                {/* Filter Controls - Fixed 300px */}
                <div className="w-full md:w-[300px] md:flex-shrink-0 space-y-4">
                  <div className="flex flex-col gap-2">
                    <Label>{t('branchSelect.selectBranch')}</Label>
                    <SingleBranchSelect
                      selectedBranch={additionSelections.branch}
                      onSelectBranch={handleBranchSelect}
                    />
                  </div>

                  {/* Show KPI and User selectors only when metadata is loaded */}
                  {metadata && (
                    <>
                      <div className="flex flex-col gap-2">
                        <Label>{t('targets.form.kpi')}</Label>
                        <TreeSelector
                          data={kpiTreeData}
                          selectedId={selectedKpiId}
                          onSelect={handleKpiTreeSelection}
                          placeholder={t('targets.form.selectKpi')}
                          loading={loading}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label>{t('targets.table.user')}</Label>
                        <TreeSelector
                          data={userFlatData}
                          selectedId={selectedUserId}
                          onSelect={handleUserSelection}
                          placeholder={t('common.labels.selectEmployee')}
                          loading={loading}
                          noTree
                          enableSearch
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label htmlFor="bonusAmount">
                          {t('targets.table.bonus')}
                        </Label>
                        <Input
                          id="bonusAmount"
                          type="number"
                          min="0"
                          step="any"
                          value={bonusAmount === 0 ? '' : bonusAmount}
                          onChange={(e) =>
                            setBonusAmount(
                              e.target.value === '' ? 0 : Number(e.target.value)
                            )
                          }
                          placeholder="0"
                          className="w-[200px]"
                        />
                      </div>

                      {/* Action Button */}
                      <div className="flex gap-4 pt-2">
                        <Button
                          onClick={handleAddKPI}
                          size="sm"
                          disabled={!isFormValid || loading}
                          className="w-[200px]"
                        >
                          <IconCirclePlus className="mr-2" />
                          {t('targets.form.addUserKpiGoal')}
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* Table/EmptyBox Section - Takes remaining space */}
                <div className="flex-1 min-w-0">
                  {!additionSelections.branch ? (
                    <ScalrEmptyBox
                      icon={IconFilter as any}
                      title={t('targets.emptyStates.selectBranchTitle')}
                      message={t('targets.emptyStates.selectBranchMessage')}
                      size="md"
                    />
                  ) : (
                    <BasicTable
                      data={kpiUserAssignments.map((assignment) => {
                        return {
                          id: assignment.id,
                          branch: assignment.branchName,
                          kpiLabel: assignment.kpiLabel,
                          user: assignment.userName,
                          target: formatValueForKpi(
                            assignment.targetValue,
                            assignment.kpiUnit
                          ),
                          bonus: assignment.bonusAmount,
                          action: (
                            <Button
                              variant="ghost"
                              onClick={() => handleRemoveKPI(assignment.id)}
                              size="sm"
                              className="h-8 w-8 p-0 bg-white border-none"
                            >
                              <IconTrash />
                            </Button>
                          ) as any,
                        }
                      })}
                      columns={[
                        ...getKpiAddFormColumns(t),
                        {
                          accessorKey: 'action',
                          header: '',
                          type: 'text',
                        },
                      ]}
                      title=""
                      loading={false}
                      enablePagination
                      itemsPerPage={6}
                      height={400}
                    />
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            disabled={kpiUserAssignments.length === 0 || isSaving}
            type="submit"
            onClick={handleSave}
          >
            {t('common.buttons.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
